//// src/admin/components/CategoryModal.tsx (fixed without DynamicIcon – use assertion)
//import React from 'react';
//import { CategoryDto, CreateCategoryDto, UpdateCategoryDto } from '../../types';
//import * as Lucide from 'lucide-react'; // For preview
//import type { LucideProps } from 'lucide-react'; // For props

//// Helper for dynamic icon
//const getDynamicIcon = (name: string | undefined): ((props: LucideProps) => JSX.Element) | undefined => {
//    if (!name || !name.trim()) return undefined;
//    const icon = (Lucide as unknown as Record<string, (props: LucideProps) => JSX.Element>)[name];
//    if (icon) {
//        return icon;
//    }
//    return undefined;
//};

//interface CategoryModalProps {
//    isOpen: boolean;
//    onClose: () => void;
//    isCreating: boolean;
//    selectedCategory: CategoryDto | null;
//    createFormData: CreateCategoryDto;
//    setCreateFormData: React.Dispatch<React.SetStateAction<CreateCategoryDto>>;
//    updateFormData: UpdateCategoryDto;
//    setUpdateFormData: React.Dispatch<React.SetStateAction<UpdateCategoryDto>>;
//    handleSaveCategory: () => Promise<void>;
//}

//const CategoryModal: React.FC<CategoryModalProps> = ({
//    isOpen,
//    onClose,
//    isCreating,
//    selectedCategory,
//    createFormData,
//    setCreateFormData,
//    updateFormData,
//    setUpdateFormData,
//    handleSaveCategory,
//}) => {
//    if (!isOpen) return null;

//    const handleSubmit = async () => {
//        try {
//            await handleSaveCategory();
//        } catch (err) {
//            alert('Save failed');
//        }
//    };

//    const iconValue = isCreating ? createFormData.icon : updateFormData.icon ?? selectedCategory?.icon ?? '';

//    const renderIconPreview = (name: string | undefined) => {
//        if (!name || !name.trim()) return <span className="text-gray-500">No icon</span>;
//        const Icon = getDynamicIcon(name);
//        if (Icon) {
//            return <Icon className="w-6 h-6 text-gray-800" />;
//        }
//        return <span className="text-red-500">Invalid icon name</span>;
//    };

//    return (
//        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
//            <div className="flex justify-between items-center mb-6">
//                <h2 className="text-xl font-bold text-gray-800">{isCreating ? 'Create Category' : 'Edit Category'}</h2>
//                <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">✕</button>
//            </div>
//            <div className="grid grid-cols-1 gap-4">
//                <div>
//                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
//                    <input
//                        type="text"
//                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
//                        value={isCreating ? createFormData.name : updateFormData.name ?? selectedCategory?.name ?? ''}
//                        onChange={(e) => {
//                            if (isCreating) setCreateFormData({ ...createFormData, name: e.target.value });
//                            else setUpdateFormData({ ...updateFormData, name: e.target.value });
//                        }}
//                    />
//                </div>
//                <div> {/* New icon field */}
//                    <label className="block text-sm font-medium text-gray-700 mb-1">Icon Name (optional, lucide-react e.g., 'shirt' – see https://lucide.dev/icons)</label>
//                    <input
//                        type="text"
//                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
//                        value={iconValue ?? ''} // Handle undefined
//                        onChange={(e) => {
//                            if (isCreating) setCreateFormData({ ...createFormData, icon: e.target.value });
//                            else setUpdateFormData({ ...updateFormData, icon: e.target.value });
//                        }}
//                    />
//                    <div className="mt-2 flex items-center gap-2">
//                        <span>Preview:</span>
//                        {renderIconPreview(iconValue)}
//                    </div>
//                </div>
//            </div>
//            <div className="mt-6 flex justify-end">
//                <button onClick={handleSubmit} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium mr-3">Save</button>
//                <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors font-medium">Cancel</button>
//            </div>
//        </div>
//    );
//};

//export default CategoryModal;
// src/admin/components/CategoryModal.tsx (with emoji-picker-react for all emojis)
import React, { useState } from 'react';
import { CategoryDto, CreateCategoryDto, UpdateCategoryDto } from '../../types';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'; // Import picker
import { ValidationError } from './ValidationError';
import AdminModalShell from './AdminModalShell';

interface CategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    isCreating: boolean;
    selectedCategory: CategoryDto | null;
    createFormData: CreateCategoryDto;
    setCreateFormData: React.Dispatch<React.SetStateAction<CreateCategoryDto>>;
    updateFormData: UpdateCategoryDto;
    setUpdateFormData: React.Dispatch<React.SetStateAction<UpdateCategoryDto>>;
    handleSaveCategory: () => Promise<void>;
}

const CategoryModal: React.FC<CategoryModalProps> = ({
    isOpen,
    onClose,
    isCreating,
    selectedCategory,
    createFormData,
    setCreateFormData,
    updateFormData,
    setUpdateFormData,
    handleSaveCategory,
}) => {
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    if (!isOpen) return null;

    const handleSubmit = async () => {
        setErrors({});
        const name = isCreating ? createFormData.name : updateFormData.name ?? selectedCategory?.name ?? '';
        if (!name || !name.trim()) {
            setErrors({ Name: 'Name is required' });
            return;
        }
        try {
            await handleSaveCategory();
            // parent may close or we can, but let parent logic decide; on success no error
        } catch (err: any) {
            // try to surface backend
            if (err?.response?.data?.errors) {
                setErrors(err.response.data.errors);
            } else {
                setErrors({ General: 'Save failed' });
            }
        }
    };

    const iconValue = isCreating ? createFormData.icon : updateFormData.icon ?? selectedCategory?.icon ?? '';

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        if (isCreating) setCreateFormData({ ...createFormData, icon: emojiData.emoji });
        else setUpdateFormData({ ...updateFormData, icon: emojiData.emoji });
    };

    const renderIconPreview = (name: string | undefined) => {
        if (!name || !name.trim()) return <span className="text-gray-500">No icon</span>;
        return <span className="text-2xl">{name}</span>; // Emoji preview
    };

    return (
        <AdminModalShell
            title={isCreating ? 'Create Category' : 'Edit Category'}
            onClose={onClose}
            size="md"
        >
            <div className="grid grid-cols-1 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                        value={isCreating ? createFormData.name : updateFormData.name ?? selectedCategory?.name ?? ''}
                        onChange={(e) => {
                            if (isCreating) setCreateFormData({ ...createFormData, name: e.target.value });
                            else setUpdateFormData({ ...updateFormData, name: e.target.value });
                        }}
                    />
                    <ValidationError error={errors.Name || errors.General} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Icon Emoji (optional – select from picker below)</label>
                    <EmojiPicker onEmojiClick={handleEmojiClick} /> {/* Picker for all emojis */}
                    <div className="mt-2 flex items-center gap-2">
                        <span>Selected Preview:</span>
                        {renderIconPreview(iconValue)}
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default package size band (optional)
                    </label>
                    <input
                        type="text"
                        maxLength={20}
                        placeholder="e.g. M or XL"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono uppercase text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                        value={
                            isCreating
                                ? createFormData.defaultPackageSizeBandCode ?? ''
                                : updateFormData.defaultPackageSizeBandCode ??
                                  selectedCategory?.defaultPackageSizeBandCode ??
                                  ''
                        }
                        onChange={(e) => {
                            const v = e.target.value.toUpperCase();
                            if (isCreating)
                                setCreateFormData({ ...createFormData, defaultPackageSizeBandCode: v });
                            else
                                setUpdateFormData({
                                    ...updateFormData,
                                    defaultPackageSizeBandCode: v,
                                });
                        }}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        Pre-selects this package size when sellers pick this category (they can still
                        change it). Must match an active band code (S, M, L, XL, or custom).
                    </p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum package size band (optional)
                    </label>
                    <input
                        type="text"
                        maxLength={20}
                        placeholder="e.g. L or XL — leave empty for no floor"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono uppercase text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                        value={
                            isCreating
                                ? createFormData.minPackageSizeBandCode ?? ''
                                : updateFormData.minPackageSizeBandCode ??
                                  selectedCategory?.minPackageSizeBandCode ??
                                  ''
                        }
                        onChange={(e) => {
                            const v = e.target.value.toUpperCase();
                            if (isCreating)
                                setCreateFormData({ ...createFormData, minPackageSizeBandCode: v });
                            else
                                setUpdateFormData({
                                    ...updateFormData,
                                    minPackageSizeBandCode: v,
                                });
                        }}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        Sellers cannot pick a smaller size for this category (e.g. XL for bulky).
                        Leave empty = no minimum.
                    </p>
                </div>
            </div>
            <div className="mt-6 flex justify-end">
                <button onClick={handleSubmit} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium mr-3">Save</button>
                <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors font-medium">Cancel</button>
            </div>
        </AdminModalShell>
    );
};

export default CategoryModal;